import {TabContext, TabPanel} from '@material-ui/lab';
import {
  ITicket,
  IWorkFlowStatus
} from '@shared/services/service-mappers/WorkflowMapper';
import React, {useEffect, useState} from 'react';
import TicketActivityTab from './ActivityTab/TicketActivityTab';
import TicketAllActivity from './TicketAllActivity/TicketAllActivity';
import TicketComment from './Comments/TicketComments';
import TicketHistory from './History/TicketHistory';
import './TicketingSystemTicketActivity.scss';
import {
  TICKETING_SYSTEM_ACTIVITY_TAB_VALUE,
  TICKETING_SYSTEM_ACTIVITY_TABS,
  TICKETING_SYSTEM_COMMON_PERMISSIONS,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import {useTranslation} from 'react-i18next';
import {getClinicUsersList} from '@shared/services/ClinicServices';
import {useSnackbar} from 'notistack';
import {UserModelMapper} from '@shared/services/service-mappers/ModelMapper';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {getTicketActivity} from '@shared/services/WorkflowServices';
import {PAGINATION_SIZE} from 'src/constants/SystemConstants';
import {IActivitySession} from '../TicketingSystemTicketDetails';
import TicketWorkLog from './WorkLogTab/TicketWorkLog';
import {CommunicationService} from 'src/service/CommunicationService';
import {Subscription} from 'rxjs';
import useTicketingSystemPermission, {
  IAllPossibleTicketModules,
  IGroupPermission
} from '../../../hooks/useTicketingSystemPermission';
import {ITicketingSystemTicketActivityItemProps} from './TicketActivityItem/TicketActivityItem';
import {Link, Tooltip} from '@material-ui/core';
import AddWorkLogTimeDialog from '../../../TicketingSystemTimer/AddWorkLogTimeDialog/AddWorkLogTimeDialog';
interface ITicketingSystemTicketActivityProps {
  ticketId: ITicket['id'];
  clinicGroupId: number;
  clinicId: number;
  statuses: IWorkFlowStatus[];
  totalRecords(value: number): any;
  pageNumber: number;
  updateActivitySection(value: Partial<IActivitySession>): void;
  componentKey: number;
  refreshTicketGrid: () => void;
  module: IAllPossibleTicketModules;
  groupPermission?: IGroupPermission;
  renderTicketActivityItemContent?: ITicketingSystemTicketActivityItemProps['renderTicketActivityItemContent'];
}
const TicketingSystemTicketActivity = (
  props: ITicketingSystemTicketActivityProps
): JSX.Element => {
  const {
    ticketId,
    clinicGroupId,
    clinicId,
    statuses,
    totalRecords,
    updateActivitySection,
    pageNumber,
    componentKey,
    refreshTicketGrid,
    groupPermission,
    module,
    renderTicketActivityItemContent
  } = props;
  const [selectedActivityType, setSelectedActivityType] = useState<string>(
    TICKETING_SYSTEM_ACTIVITY_TAB_VALUE.COMMENTS
  );
  const subscription = React.useRef<Subscription>();

  const {t} = useTranslation();
  const activities = TICKETING_SYSTEM_ACTIVITY_TABS.map(i => ({
    ...i,
    label: t(i.label)
  }));
  const [dataList, setDataList] = useState<{
    data: any[];
    isLoading: boolean;
  }>({
    data: [],
    isLoading: true
  });
  const [AddWorkLogTime, setAddWorkLogTime] = useState(false);

  const handleSelectActivityType = (event: any, type: any) => {
    setSelectedActivityType(type);
    updateActivitySection({pageNumber: 1});
    setDataList(() => ({
      data: [],
      isLoading: false
    }));
  };

  const {enqueueSnackbar} = useSnackbar();

  const [commonData, setCommonData] = useState<{
    clinicUsersList: UserModelMapper[];
  }>({
    clinicUsersList: []
  });

  const {clinicUsersList} = commonData;

  useEffect(() => {
    const init = async () => {
      try {
        const response = await getClinicUsersList(clinicGroupId, clinicId);
        if (response.data) {
          setCommonData(p => ({...p, clinicUsersList: response.data}));
        } else {
          setCommonData(p => ({...p, clinicUsersList: []}));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, clinicGroupId]);

  const fetchTabsDataOfActionHistory = async () => {
    try {
      setDataList(prev => ({
        ...prev,
        isLoading: true
      }));
      const params: Parameters<typeof getTicketActivity>[0]['params'] = {
        module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
      };
      if (selectedActivityType !== TICKETING_SYSTEM_ACTIVITY_TAB_VALUE.ALL) {
        params.type = selectedActivityType;
      }
      params.limit = PAGINATION_SIZE.TICKETING_ACTION_HISTORY;
      if (pageNumber) {
        params.offset = (pageNumber - 1) * params.limit;
      }
      const dataListResponse = await getTicketActivity({
        clinicGroupId,
        clinicId,
        tickedId: ticketId,
        params
      });
      if (dataListResponse.data.rows) {
        if (pageNumber > 1) {
          setDataList(prev => ({
            ...prev,
            data: [...prev.data, ...dataListResponse.data.rows]
          }));
        } else {
          setDataList(prev => ({
            ...prev,
            data: dataListResponse.data.rows
          }));
        }
        totalRecords(dataListResponse.data.count);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    } finally {
      setDataList(prev => ({
        ...prev,
        isLoading: false
      }));
    }
  };

  useEffect(() => {
    fetchTabsDataOfActionHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clinicGroupId,
    clinicId,
    ticketId,
    selectedActivityType,
    componentKey,
    pageNumber
  ]);

  // NOTE: ** if the status gets updated in the ticket, then this useEffect
  // will execute to get the updated API response and fetches the latest ticket details **//
  useEffect(() => {
    // if there is an update available and no state passed
    subscription.current = CommunicationService.getEvent().subscribe(data => {
      if (data.action === 'TICKET_TIMER_PANEL_DATA_UPDATED') {
        fetchTabsDataOfActionHistory();
      }
    });
    return () => {
      subscription.current?.unsubscribe();
    };
    // eslint-disable-next-line
  }, [selectedActivityType]);

  // ** Permission to check the add work log link manullay */
  const hasPriorAuthAddWorkLogLink = useTicketingSystemPermission({
    module,
    groupPermission,
    objectFunction: TICKETING_SYSTEM_COMMON_PERMISSIONS.CREATE_WORK_LOG_MANUALLY
  });

  const renderAddWorkLogButton = () => {
    if (hasPriorAuthAddWorkLogLink) {
      return (
        <div>
          <Tooltip
            title={t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.TITLE').toString()}>
            <div>
              <Link
                className="action"
                onClick={(e: any) => {
                  setAddWorkLogTime(true);
                  e.stopPropagation();
                }}>
                {t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.TITLE')}
              </Link>
            </div>
          </Tooltip>
        </div>
      );
    }
  };

  return (
    <div className="ticketing-system-ticket-activities-wrapper">
      <p className="ticketing-system-ticket-activities-header-wrapper">
        <p className={'section-title'}>
          {t('TICKETING_SYSTEM.ACTIVITY.TITLE')}
        </p>
        <p>{renderAddWorkLogButton()}</p>
      </p>
      <TabContext value={selectedActivityType}>
        <TicketActivityTab
          value={selectedActivityType}
          onChange={handleSelectActivityType}
          tabs={activities}
        />
        <TabPanel
          className="tab-panel"
          value={TICKETING_SYSTEM_ACTIVITY_TAB_VALUE.COMMENTS}>
          <TicketComment
            key={componentKey}
            clinicGroupId={clinicGroupId}
            clinicId={clinicId}
            tickedId={ticketId}
            selectedDataList={dataList}
            handleCommentAction={() => {
              updateActivitySection({
                pageNumber: 1,
                updateComponentKey: componentKey + 1
              });
              refreshTicketGrid();
            }}
            module={module}
            groupPermission={groupPermission}
            renderTicketActivityItemContent={renderTicketActivityItemContent}
          />
        </TabPanel>
        <TabPanel
          className="tab-panel"
          value={TICKETING_SYSTEM_ACTIVITY_TAB_VALUE.HISTORY}>
          <TicketHistory
            key={componentKey}
            clinicUsersList={clinicUsersList}
            statuses={statuses}
            selectedDataList={dataList}
            renderTicketActivityItemContent={renderTicketActivityItemContent}
          />
        </TabPanel>
        <TabPanel
          className="tab-panel"
          value={TICKETING_SYSTEM_ACTIVITY_TAB_VALUE.ALL}>
          <TicketAllActivity
            key={componentKey}
            clinicUsersList={clinicUsersList}
            statuses={statuses}
            selectedDataList={dataList}
            renderTicketActivityItemContent={renderTicketActivityItemContent}
          />
        </TabPanel>
        <TabPanel
          className="tab-panel"
          value={TICKETING_SYSTEM_ACTIVITY_TAB_VALUE.TIME_LOGS}>
          <TicketWorkLog
            key={componentKey}
            selectedDataList={dataList}
            renderTicketActivityItemContent={renderTicketActivityItemContent}
          />
        </TabPanel>
      </TabContext>
      {AddWorkLogTime && (
        <AddWorkLogTimeDialog
          open={AddWorkLogTime}
          onClose={() => {
            setAddWorkLogTime(false);
          }}
          clinicGroupId={clinicGroupId}
          clinicId={clinicId}
          patientTicketId={ticketId as number}
          module={module}
          handleWorkLogAction={() => {
            updateActivitySection({
              pageNumber: 1,
              updateComponentKey: componentKey + 1
            });
            // refreshTicketGrid();
          }}
        />
      )}
    </div>
  );
};

export default TicketingSystemTicketActivity;
